import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  listRequiredDocuments,
  type PortalRequiredDocument,
  uploadRequiredDocument,
} from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";

function formatDue(due: string | null): string | null {
  if (!due) return null;
  const d = new Date(`${due}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** One requested document — name, status, and (while pending) an upload control. */
function RequestedDocRow({
  doc,
  onUploaded,
}: {
  doc: PortalRequiredDocument;
  onUploaded: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const received = doc.status === "RECEIVED";
  const due = formatDue(doc.dueDate);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await uploadRequiredDocument(doc.id, file);
      toast.success(`"${doc.name}" sent to your firm.`);
      onUploaded();
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ${
            received ? "bg-success-soft text-success" : "bg-warning-soft text-warning"
          }`}
        >
          {received ? <Check className="size-4" /> : <Clock className="size-4" />}
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate font-medium text-foreground">{doc.name}</span>
          {doc.notes && <span className="text-muted-foreground text-xs">{doc.notes}</span>}
          <span className="text-muted-foreground text-xs">
            {received ? "Received — thank you" : due ? `Due ${due}` : "Requested"}
          </span>
        </span>
      </div>
      {received ? (
        <span className="text-success text-sm font-medium">Done</span>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
            Upload
          </Button>
          <input ref={inputRef} type="file" className="hidden" onChange={onPick} />
        </>
      )}
    </div>
  );
}

/** `/portal/requested-documents` — the documents the firm asked this client to
 * provide; upload against each and it goes straight to the firm. */
export function PortalRequestedDocs() {
  const [docs, setDocs] = useState<PortalRequiredDocument[] | null>(null);

  // Post-upload refresh — the upload handler already toasts on its own failure,
  // so a refresh hiccup here just keeps the current list.
  const refetch = useCallback(async () => {
    try {
      setDocs(await listRequiredDocuments());
    } catch {
      /* keep the current list */
    }
  }, []);

  // Initial load — surface a failure instead of rendering an empty "nothing
  // requested" state, and guard against setting state after unmount.
  useEffect(() => {
    let active = true;
    listRequiredDocuments()
      .then((list) => active && setDocs(list))
      .catch(() => {
        if (!active) return;
        setDocs((prev) => prev ?? []);
        toast.error("Couldn't load your requested documents. Please refresh.");
      });
    return () => {
      active = false;
    };
  }, []);

  if (docs === null) return <PageLoader />;

  const pending = docs.filter((d) => d.status !== "RECEIVED");

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 py-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Requested Documents</h1>
        <Button asChild variant="outline">
          <Link to={portalHref("/documents/upload")}>Upload other files</Link>
        </Button>
      </div>

      {docs.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground text-sm">
            Your firm hasn't requested any documents yet. When they do, they'll appear here.
          </p>
        </Card>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">
            {pending.length > 0
              ? `${pending.length} ${pending.length === 1 ? "document" : "documents"} still needed. Upload each below — it goes straight to your firm.`
              : "Everything your firm requested has been received. Thank you!"}
          </p>
          <Card className="gap-0 divide-y overflow-hidden p-0">
            {docs.map((doc) => (
              <RequestedDocRow key={doc.id} doc={doc} onUploaded={refetch} />
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
